/**
 * Buy New Leads Hook — rollout flag + order kickoff.
 *
 * enabled (rollout gate, Option B / allowlist-then-default-on, design §4.9 / §7 #12):
 *   effective = clients/{effectiveClientId}.buyLeadsEnabled (boolean | undefined)
 *               ?? config/features.buyLeadsDefault (boolean, default false)
 *   A per-client boolean always wins (the individual off-switch, even `false`);
 *   only when the field is absent do we fall through to the global default.
 *   FAIL CLOSED: any snapshot error / missing doc resolves to disabled.
 *
 * startOrder: mints an order via createLeadOrder and opens the jadenterprises.org
 *   checkout copy in a new tab. Swallows its own errors so it can be wired
 *   directly as an onClick handler.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import { useEffectiveClientId } from './useImpersonation';

export function useBuyLeads() {
  const effectiveClientId = useEffectiveClientId();
  // undefined = per-client field absent → fall through to the global default.
  const [perClient, setPerClient] = useState<boolean | undefined>(undefined);
  const [globalDefault, setGlobalDefault] = useState<boolean>(false);

  // Per-client override: clients/{id}.buyLeadsEnabled
  useEffect(() => {
    if (!effectiveClientId) { setPerClient(undefined); return; }
    const ref = doc(db, 'clients', effectiveClientId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const v = snap.exists() ? snap.data()?.buyLeadsEnabled : undefined;
        setPerClient(typeof v === 'boolean' ? v : undefined);
      },
      (err) => {
        console.error('buyLeads client-flag listener error:', err);
        setPerClient(undefined); // fail closed → global default (false) governs
      }
    );
    return () => unsub();
  }, [effectiveClientId]);

  // Global default: config/features.buyLeadsDefault (default false until proven).
  useEffect(() => {
    const ref = doc(db, 'config', 'features');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const v = snap.exists() ? snap.data()?.buyLeadsDefault : undefined;
        setGlobalDefault(v === true);
      },
      (err) => {
        console.error('buyLeads global-default listener error:', err);
        setGlobalDefault(false); // fail closed
      }
    );
    return () => unsub();
  }, []);

  const enabled = perClient ?? globalDefault;

  // Single-flight + reuse: rapid re-clicks while the callable is cold
  // (~10-30s) must NOT mint extra draft orders (each stray draft would need
  // sweeper cleanup — 4 minted in the 2026-07-18 canary). Reuse is
  // CROSS-SESSION via localStorage: a curious clicker who circles back days
  // later reopens their existing draft (verified still open in Firestore)
  // instead of minting another.
  const [starting, setStarting] = useState(false);
  const inFlightRef = useRef(false);
  const mintedUrlRef = useRef<string | null>(null);

  const storageKey = effectiveClientId ? `jad_buyleads_draft_${effectiveClientId}` : null;

  const startOrder = useCallback(async () => {
    if (inFlightRef.current) return;

    // Chrome popup-blocker rule: window.open is only allowed synchronously
    // inside the click gesture. Minting the order first (~10-30s cold) and
    // THEN opening got blocked (caught live 2026-07-18). So: open the tab
    // NOW with a branded interstitial, and point it at Stripe once the order
    // exists. If even the synchronous open is blocked, fall back to
    // navigating THIS tab — the buyer always reaches checkout.
    const win = window.open('', '_blank');
    if (win) {
      try {
        win.document.write(
          '<body style="margin:0;display:flex;align-items:center;justify-content:center;' +
          'height:100vh;background:#111318;color:#e5e7eb;font-family:Arial,sans-serif;">' +
          '<div style="text-align:center;"><div style="font-size:1.1rem;font-weight:bold;' +
          'color:#d3a625;margin-bottom:10px;">JAD Enterprises</div>' +
          '<div>Preparing your secure checkout…</div>' +
          '<div style="color:#6b7280;font-size:0.85rem;margin-top:8px;">This can take a few seconds.</div>' +
          '</div></body>',
        );
        win.document.title = 'Preparing checkout…';
      } catch { /* best-effort interstitial */ }
    }
    const go = (url: string) => {
      if (win && !win.closed) {
        win.location.replace(url);
      } else {
        window.location.assign(url);   // popup blocked (or closed) — same-tab
      }
    };
    const abort = () => { if (win && !win.closed) win.close(); };

    if (mintedUrlRef.current) {
      go(mintedUrlRef.current);
      return;
    }
    inFlightRef.current = true;
    setStarting(true);
    try {
      // Cross-session reuse: a stored draft that is still open wins.
      if (storageKey) {
        try {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const { orderId, checkoutUrl } = JSON.parse(stored) as { orderId?: string; checkoutUrl?: string };
            if (orderId && checkoutUrl && effectiveClientId) {
              const snap = await getDoc(doc(db, 'clients', effectiveClientId, 'leadOrders', orderId));
              const status = snap.exists() ? (snap.data()?.status as string) : null;
              if (status === 'draft' || status === 'awaiting_payment') {
                mintedUrlRef.current = checkoutUrl;
                go(checkoutUrl);
                return;
              }
              localStorage.removeItem(storageKey); // stale (paid/cancelled/missing)
            }
          }
        } catch { /* storage/read issues — fall through to minting */ }
      }

      const fn = httpsCallable(functions, 'createLeadOrder');
      const result = await fn({});
      const data = result.data as { orderId?: string; checkoutUrl?: string };
      if (data?.checkoutUrl) {
        mintedUrlRef.current = data.checkoutUrl;
        if (storageKey && data.orderId) {
          try {
            localStorage.setItem(storageKey, JSON.stringify({ orderId: data.orderId, checkoutUrl: data.checkoutUrl }));
          } catch { /* quota — non-fatal */ }
        }
        go(data.checkoutUrl);
      } else {
        abort();
      }
    } catch (err) {
      console.error('createLeadOrder failed:', err);
      abort();
    } finally {
      inFlightRef.current = false;
      setStarting(false);
    }
  }, [storageKey, effectiveClientId]);

  return { enabled, startOrder, starting };
}
